import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Instagram, Facebook, Youtube, MapPin } from 'lucide-react';
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProgramsOpen, setIsProgramsOpen] = useState(false);
  const location = useLocation();
  const navigation = [{
    name: 'Home',
    href: '/'
  }, {
    name: 'About',
    href: '/about'
  }, {
    name: 'Programs / Classes',
    href: '/programs',
    hasDropdown: true,
    submenu: [{
      name: 'Brazilian Jiu-Jitsu',
      href: '/programs/brazilian-jiu-jitsu'
    }, {
      name: 'Kids Brazilian Jiu-Jitsu',
      href: '/programs/kids-brazilian-jiu-jitsu'
    }, {
      name: 'Muay Thai',
      href: '/programs/muay-thai'
    }, {
      name: 'Boxing',
      href: '/programs/boxing'
    }, {
      name: 'Kickboxing',
      href: '/programs/kickboxing'
    }, {
      name: 'MMA Training',
      href: '/programs/mma-training'
    }, {
      name: 'Personal Training',
      href: '/programs/personal-training'
    }, {
      name: 'Adopt A Cop',
      href: '/programs'
    }]
  }, {
    name: 'Schedule',
    href: '/schedule'
  }, {
    name: 'Instructors',
    href: '/instructors'
  }, {
    name: 'Free Class',
    href: '/free-class'
  }, {
    name: 'Contact',
    href: '/contact'
  }];
  const socialLinks = [{
    name: 'Instagram',
    href: 'https://www.instagram.com/mashgym/',
    icon: Instagram
  }, {
    name: 'Facebook',
    href: 'http://facebook.com/mashgym',
    icon: Facebook
  }, {
    name: 'YouTube',
    href: 'https://www.youtube.com/c/MashGym',
    icon: Youtube
  }];
  const isActive = (path: string) => {
    if (path === '/' && location.pathname !== '/') return false;
    return location.pathname.startsWith(path);
  };
  return <>
      {/* Eyebrow Bar - Static, Not Floating */}
      <div className="bg-gray-50 border-b border-gray-100" data-id="element-101">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-102">
          <div className="py-2 text-center" data-id="element-103">
            <p className="text-xs sm:text-sm text-gray-600 font-medium" data-id="element-104">
              MMA Gym • Brazilian Jiu-Jitsu • Muay Thai • Boxing • Serving
              Redford & Metro Detroit
            </p>
          </div>
        </div>
      </div>

      {/* Main Header - Floating/Sticky */}
      <header className="bg-white shadow-sm sticky top-0 z-50" data-id="element-105">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-106">
          <div className="flex justify-between items-center h-28" data-id="element-107">
            {/* Left Section - Logo + Address */}
            <div className="flex items-center gap-1.5" data-id="element-108">
              {/* Logo */}
              <div className="flex-shrink-0" data-id="element-109">
                <Link to="/" data-id="element-110">
                  <img className="h-24 w-auto" src="https://drwzpk38qkpfb.cloudfront.net/www.mashgym.com/uploaded/images/mash_gym_logo_NEW_red.png" alt="Mash Gym" data-id="element-111" />
                </Link>
              </div>

              {/* Address Block */}
              <a href="https://www.google.com/maps/search/?api=1&query=26575+8+Mile+Road+Redford+Michigan+48240" target="_blank" rel="noopener noreferrer" className="hidden lg:flex items-start space-x-2 group" data-id="element-112">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0 group-hover:text-red-600 transition-colors" data-id="element-113" />
                <div className="text-xs leading-tight" data-id="element-114">
                  <div className="text-gray-600 group-hover:text-gray-900 transition-colors" data-id="element-115">
                    26575 8 Mile Road
                  </div>
                  <div className="text-gray-500 group-hover:text-gray-700 transition-colors" data-id="element-116">
                    Redford, Michigan 48240
                  </div>
                </div>
              </a>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-8" data-id="element-117">
              {navigation.map(item => <div key={item.name} className="relative" data-id="element-118">
                  {item.hasDropdown ? <div className="relative" onMouseEnter={() => setIsProgramsOpen(true)} onMouseLeave={() => setIsProgramsOpen(false)} data-id="element-119">
                      <Link to={item.href} className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200 ${isActive(item.href) ? 'border-red-600 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`} data-id="element-120">
                        {item.name}
                        <ChevronDown className="ml-1 w-4 h-4" data-id="element-121" />
                      </Link>

                      {/* Dropdown Menu */}
                      {isProgramsOpen && <div className="absolute left-0 mt-2 w-64 bg-white rounded-md shadow-xl py-2 z-50 border border-gray-100" data-id="element-122">
                          {item.submenu?.map(subItem => <Link key={subItem.name} to={subItem.href} className="block px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors" data-id="element-123">
                              {subItem.name}
                            </Link>)}
                        </div>}
                    </div> : <Link to={item.href} className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200 ${isActive(item.href) ? 'border-red-600 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`} data-id="element-124">
                      {item.name}
                    </Link>}
                </div>)}
            </nav>

            {/* Right Section - Social Icons + Phone */}
            <div className="hidden lg:flex flex-col items-end space-y-2.5" data-id="element-125">
              {/* Social Icons - Red background with darker red hover */}
              <div className="flex items-center space-x-2" data-id="element-126">
                {socialLinks.map(social => {
                const Icon = social.icon;
                return <a key={social.name} href={social.href} target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-md transition-all duration-200" aria-label={social.name} data-id="element-127">
                      <Icon className="w-4 h-4" data-id="element-128" />
                    </a>;
              })}
              </div>

              {/* Phone Number */}
              <a href="tel:+13135550100" className="inline-flex items-center px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-full transition-all duration-200 shadow-sm hover:shadow-md" data-id="element-129">
                <Phone className="w-4 h-4 mr-2" data-id="element-130" />
                (313) 555-0100
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center lg:hidden" data-id="element-131">
              <button type="button" className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500" onClick={() => setIsMenuOpen(!isMenuOpen)} data-id="element-132">
                <span className="sr-only" data-id="element-133">Open main menu</span>
                {isMenuOpen ? <X className="block h-6 w-6" aria-hidden="true" data-id="element-134" /> : <Menu className="block h-6 w-6" aria-hidden="true" data-id="element-135" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && <div className="lg:hidden bg-white border-t border-gray-200" data-id="element-136">
            <div className="pt-2 pb-3 space-y-1" data-id="element-137">
              {navigation.map(item => <div key={item.name} data-id="element-138">
                  <Link to={item.href} className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive(item.href) ? 'bg-red-50 border-red-500 text-red-700' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'}`} onClick={() => !item.hasDropdown && setIsMenuOpen(false)} data-id="element-139">
                    {item.name}
                  </Link>

                  {/* Mobile Submenu */}
                  {item.hasDropdown && item.submenu && <div className="pl-8 space-y-1" data-id="element-140">
                      {item.submenu.map(subItem => <Link key={subItem.name} to={subItem.href} className="block pl-3 pr-4 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-gray-50" onClick={() => setIsMenuOpen(false)} data-id="element-141">
                          {subItem.name}
                        </Link>)}
                    </div>}
                </div>)}

              {/* Mobile Address */}
              <div className="pt-4 border-t border-gray-200" data-id="element-142">
                <a href="https://www.google.com/maps/search/?api=1&query=26575+8+Mile+Road+Redford+Michigan+48240" target="_blank" rel="noopener noreferrer" className="flex items-start mx-3 px-4 py-3 bg-gray-50 rounded-md" data-id="element-143">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-144" />
                  <div className="text-sm" data-id="element-145">
                    <div className="text-gray-700 font-medium" data-id="element-146">
                      26575 8 Mile Road
                    </div>
                    <div className="text-gray-500" data-id="element-147">Redford, Michigan 48240</div>
                  </div>
                </a>
              </div>

              {/* Mobile Phone + Social */}
              <div className="pt-4 pb-3 border-t border-gray-200" data-id="element-148">
                <a href="tel:+13135550100" className="flex items-center justify-center mx-3 px-4 py-3 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-md transition-all" data-id="element-149">
                  <Phone className="w-4 h-4 mr-2" data-id="element-150" />
                  (313) 555-0100
                </a>

                <div className="flex justify-center space-x-3 mt-4" data-id="element-151">
                  {socialLinks.map(social => {
                const Icon = social.icon;
                return <a key={social.name} href={social.href} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-md transition-all" aria-label={social.name} data-id="element-152">
                        <Icon className="w-5 h-5" data-id="element-153" />
                      </a>;
              })}
                </div>
              </div>
            </div>
          </div>}
      </header>
    </>;
}